/**
 * APRESENTAÇÃO SMS — os dois modelos por tipo de lead (Douglas 02/10/2026).
 *
 * A = urgente (o que está em jogo para o cliente) · B = o consultor fala.
 * "Vencida" só aparece na RENOVAÇÃO CLARO: nos outros tipos não é verdade.
 *
 * ⛔ SEM ACENTO de propósito: um único "ç" ou "ã" troca a codificação do SMS e
 * o limite cai de 160 para 70 caracteres — o texto chega cortado ou em 3 partes.
 *
 * ⛔ UMA MENSAGEM SÓ (Douglas 02/10/2026): a Exodus divide em 2 partes a partir
 * de 147 caracteres (medido). O teto é 145, igual no servidor.
 *
 * O {link} vira w.empresarialprime.com.br/<código> no envio (34 caracteres):
 * o clique leva ao WhatsApp do DONO do cliente naquele momento e aparece no Click.
 */

export const LINK_SMS_EXEMPLO = 'w.empresarialprime.com.br/a1b2c3d4';
export const LIMITE_SMS = 145;

export interface ModeloSmsProposta {
  id: 'A' | 'B';
  rotulo: string;
  texto: string;
}

export function semAcento(texto: string): string {
  return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[ºª]/g, '');
}

export function tamanhoSms(texto: string): number {
  return texto.replace(/\{link\}/g, LINK_SMS_EXEMPLO).length;
}

const SUFIXOS_EMPRESA = /\b(LTDA|ME|EPP|EIRELI|S\/?A|SA|MEI|LIMITADA|COMERCIAL|COMERCIO|SERVICOS|SERVICO|INDUSTRIA|DE|DA|DO|DOS|DAS|E)\b\.?/g;

function nomesDaEmpresa(razao: string): string[] {
  const limpa = semAcento(String(razao || '')).toUpperCase().replace(/[^A-Z0-9&\s/.-]/g, ' ').replace(/\s+/g, ' ').trim();
  const semSufixo = limpa.replace(SUFIXOS_EMPRESA, ' ').replace(/\s+/g, ' ').trim();
  const palavras = (semSufixo || limpa).split(' ').filter(Boolean);
  const opcoes = [palavras.slice(0, 3).join(' '), palavras.slice(0, 2).join(' '), palavras[0] || ''];
  return Array.from(new Set(opcoes.filter(Boolean)));
}

function primeiroNome(nome: string | null | undefined): string {
  const p = semAcento(String(nome || '').trim()).split(/\s+/)[0] || '';
  return p ? p.charAt(0).toUpperCase() + p.slice(1).toLowerCase() : '';
}

const PORTABILIDADE_PJ = new Set(['PORTABILIDADE_VIVO', 'OI', 'ALGAR']);

type Gerador = (empresa: string, consultor: string) => [string, string];

function geradorDoTipo(tipoLead: string, pf: boolean): Gerador {
  if (pf) {
    if (tipoLead === 'RENOVACAO_CPF') {
      return (n, c) => [
        `${n ? `${n}, seu` : 'Seu'} plano Claro pode subir com o reajuste. Ajustamos sem aumentar sua conta. Fale com ${c}, Prime Conecta: {link}`,
        `${c}, da Prime Conecta: preparei a renovacao do seu plano Claro sem aumento na conta. Me chame: {link}`,
      ];
    }
    return (n, c) => [
      `${n ? `${n}, plano` : 'Plano'} Claro com mais internet pagando menos e mantendo seu numero. Fale com ${c}, Prime Conecta: {link}`,
      `${c}, da Prime Conecta: tenho uma condicao Claro para voce, mantendo seu numero. Me chame: {link}`,
    ];
  }
  if (tipoLead === 'RENOVACAO_CLARO') {
    return (e, c) => [
      `${e}: renovacao Claro vencida. Ajustamos seu plano sem aumentar a conta. Fale com ${c}, gerente Claro Empresas: {link}`,
      `${c}, gerente de contas Claro Empresas: a renovacao da ${e} venceu. Da para ajustar sem aumento na conta. Me chame: {link}`,
    ];
  }
  if (PORTABILIDADE_PJ.has(tipoLead)) {
    return (e, c) => [
      `${e}: traga suas linhas para a Claro mantendo os numeros e pague menos. Fale com ${c}, gerente de contas: {link}`,
      `${c}, gerente de contas Claro Empresas: preparei uma proposta para a ${e} manter os numeros e pagar menos. Me chame: {link}`,
    ];
  }
  if (tipoLead === 'TT_CPF_CNPJ') {
    return (e, c) => [
      `${e}: passe as linhas Claro do CPF para o CNPJ, mesmo numero e chip. Fale com ${c}, gerente de contas: {link}`,
      `${c}, gerente de contas Claro Empresas: preparei a passagem das linhas do CPF para o CNPJ da ${e}. Me chame: {link}`,
    ];
  }
  return (e, c) => [
    `${e}: condicao Claro Empresas para reduzir sua conta de celular e internet. Fale com ${c}, gerente de contas: {link}`,
    `${c}, gerente de contas Claro Empresas: preparei uma proposta para a ${e}. Me chame: {link}`,
  ];
}

/**
 * Os dois modelos prontos, já dentro do LIMITE_SMS: se o nome da empresa estoura, ele
 * encurta (3 palavras → 2 → 1). Nunca corta o texto nem o link.
 */
export function modelosSmsProposta(p: {
  tipoLead?: string | null;
  tipoPessoa?: string | null;
  razaoSocial?: string | null;
  nomeContato?: string | null;
  consultorNome?: string | null;
}): ModeloSmsProposta[] {
  const pf = p.tipoPessoa === 'PF';
  const tipo = String(p.tipoLead || '').toUpperCase();
  const consultor = primeiroNome(p.consultorNome) || 'seu consultor';
  const gerar = geradorDoTipo(tipo, pf);
  const nomes = pf ? [primeiroNome(p.nomeContato || p.razaoSocial), ''] : [...nomesDaEmpresa(p.razaoSocial || ''), 'SUA EMPRESA'];

  // Encurta primeiro o nome da empresa; se ainda não couber, "gerente de
  // contas" vira "gerente", depois sai o "Claro Empresas" do cargo e por
  // último o próprio cargo. O texto e
  // o link nunca são cortados.
  const encurtamentos = [
    (t: string) => t,
    (t: string) => t.replace(/gerente de contas/g, 'gerente'),
    (t: string) => t.replace(/gerente de contas/g, 'gerente').replace(/ Claro Empresas:/, ':'),
    (t: string) => t.replace(/gerente de contas/g, 'gerente').replace(/ Claro Empresas:/, ':').replace(/, (gerente|Prime Conecta):/, ':'),
  ];
  const escolher = (indice: 0 | 1) => {
    for (const encurtar of encurtamentos) {
      for (const nome of nomes) {
        const texto = encurtar(semAcento(gerar(nome, consultor)[indice]));
        if (tamanhoSms(texto) <= LIMITE_SMS) return texto;
      }
    }
    return encurtamentos[encurtamentos.length - 1](semAcento(gerar(nomes[nomes.length - 1], consultor)[indice]));
  };

  return [
    { id: 'A', rotulo: 'A · Urgente', texto: escolher(0) },
    { id: 'B', rotulo: 'B · Consultor', texto: escolher(1) },
  ];
}
